import React from 'react';
import { X, BarChart3, TrendingUp, CalendarRange, EyeOff, Lightbulb } from 'lucide-react';

const COLORS = {
  history: '#94a3b8',
  forecast: '#3b82f6',
  source: '#93c5fd'
};

// Bar with rounded top corners, square at the baseline.
const barPath = (x, yTop, w, h, r = 3) => {
  const rr = Math.min(r, h / 2, w / 2);
  return `M ${x} ${yTop + h} L ${x} ${yTop + rr} Q ${x} ${yTop} ${x + rr} ${yTop} L ${x + w - rr} ${yTop} Q ${x + w} ${yTop} ${x + w} ${yTop + rr} L ${x + w} ${yTop + h} Z`;
};

const MiniChart = ({ bars, months = [], refLine, arrow, emphasizeMonths = [], ariaLabel }) => {
  const width = 400;
  const padTop = arrow ? 40 : 26;
  const padBottom = 18;
  const padX = 10;
  const plotH = 105;
  const height = padTop + plotH + padBottom;
  const innerW = width - padX * 2;
  const band = innerW / bars.length;
  const barW = Math.min(24, band - 5);
  const maxV = Math.max(...bars.map(b => b.v), refLine ? refLine.v : 0) * 1.08;

  const xOf = (i) => padX + i * band + (band - barW) / 2;
  const yOf = (v) => padTop + plotH * (1 - v / maxV);
  const baseY = padTop + plotH;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" role="img" aria-label={ariaLabel}>
      {arrow && (
        <defs>
          <marker id="pred-arrowhead" markerWidth="7" markerHeight="7" refX="5.5" refY="3.5" orient="auto">
            <path d="M 0.5 0.5 L 5.5 3.5 L 0.5 6.5" fill="none" stroke="#60a5fa" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </marker>
        </defs>
      )}

      {/* Baseline */}
      <line x1={padX} y1={baseY} x2={width - padX} y2={baseY} stroke="#e2e8f0" strokeWidth="1" />

      {/* Reference line */}
      {refLine && (
        <g>
          <line
            x1={padX}
            y1={yOf(refLine.v)}
            x2={width - padX}
            y2={yOf(refLine.v)}
            stroke={refLine.color || '#94a3b8'}
            strokeWidth="1.5"
            strokeDasharray="4 3"
          />
          {refLine.label && (
            <text x={padX + 2} y={yOf(refLine.v) - 5} fontSize="9.5" fill="#64748b">
              {refLine.label}
            </text>
          )}
        </g>
      )}

      {/* Bars */}
      {bars.map((b, i) => (
        <path key={i} d={barPath(xOf(i), yOf(b.v), barW, baseY - yOf(b.v))} fill={COLORS[b.kind] || COLORS.history} />
      ))}

      {/* Arrow annotation (seasonal: last year's month -> forecast) */}
      {arrow && (() => {
        const x0 = xOf(arrow.from) + barW / 2;
        const y0 = yOf(bars[arrow.from].v) - 6;
        const x1 = xOf(arrow.to) + barW / 2;
        const y1 = yOf(bars[arrow.to].v) - 8;
        const cy = Math.min(y0, y1) - 26;
        return (
          <path
            d={`M ${x0} ${y0} Q ${(x0 + x1) / 2} ${cy} ${x1} ${y1}`}
            fill="none"
            stroke="#60a5fa"
            strokeWidth="1.5"
            strokeDasharray="4 3"
            markerEnd="url(#pred-arrowhead)"
          />
        );
      })()}

      {/* Value labels (selective, forecast only) */}
      {bars.map((b, i) => {
        if (!b.label) return null;
        const cx = Math.min(Math.max(xOf(i) + barW / 2, 30), width - 34);
        return (
          <text key={`l${i}`} x={cx} y={yOf(b.v) - 6} fontSize="11" fontWeight="600" fill="#334155" textAnchor="middle">
            {b.label}
          </text>
        );
      })}

      {/* Month labels */}
      {months.map((m, i) => {
        if (!m) return null;
        const strong = emphasizeMonths.includes(i);
        return (
          <text
            key={`m${i}`}
            x={xOf(i) + barW / 2}
            y={baseY + 13}
            fontSize="10"
            fill={strong ? '#334155' : '#94a3b8'}
            fontWeight={strong ? '700' : '400'}
            textAnchor="middle"
          >
            {m}
          </text>
        );
      })}
    </svg>
  );
};

const Legend = ({ items }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
    {items.map((it) => (
      <span key={it.label} className="flex items-center gap-1.5 text-[11px] text-neutral-500">
        <span className="w-2.5 h-2.5 rounded-[3px] flex-shrink-0" style={{ backgroundColor: it.color }} />
        {it.label}
      </span>
    ))}
  </div>
);

const Section = ({ icon: Icon, title, tagline, children }) => (
  <section className="bg-neutral-50 rounded-2xl p-4 space-y-3">
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 bg-white rounded-xl border border-neutral-200 flex items-center justify-center flex-shrink-0">
        <Icon size={18} className="text-primary-600" />
      </div>
      <div>
        <h3 className="text-sm font-bold text-neutral-800">{title}</h3>
        <p className="text-xs text-neutral-400">{tagline}</p>
      </div>
    </div>
    {children}
  </section>
);

const ChartCard = ({ legend, caption, children }) => (
  <div className="bg-white rounded-xl border border-neutral-200 p-3 space-y-2">
    <Legend items={legend} />
    {children}
    <p className="text-[11px] text-neutral-400 leading-snug">{caption}</p>
  </div>
);

const DECISION_ROWS = [
  { q: 'Jeden Monat ungefähr gleich viel?', a: 'Konstanter Durchschnitt' },
  { q: 'Niveau hat sich kürzlich geändert?', a: 'Aktuelles Niveau' },
  { q: 'Schwankt gleichmäßig übers Jahr?', a: 'Saisonale Schwankung' },
  { q: 'Selten, unregelmäßig?', a: 'Nicht vorhersagen' }
];

const PredictionHelpModal = ({ onClose }) => {
  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[60]"
      onClick={(e) => { e.stopPropagation(); onClose(); }}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-xl overflow-hidden border border-neutral-100 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 flex-none">
          <div>
            <h2 className="text-lg font-bold text-neutral-800">Vorhersagen verstehen</h2>
            <p className="text-xs text-neutral-400 mt-0.5">Wann welcher Vorhersagetyp sinnvoll ist</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-xl transition-all"
          >
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-4">
          <p className="text-sm text-neutral-600 leading-relaxed">
            Für jede Kategorie kann man auswählen, ob und wie Ausgaben geschätzt werden. Die Vorhersage
            erscheint im Dashboard bei aktuellen und zukünftigen Monaten und hilft beim Planen.
          </p>

            {/* Decision guide */}
            <div className="bg-primary-50/60 border border-primary-100 rounded-2xl p-4 space-y-2">
            <div className="flex items-center gap-2">
                <Lightbulb size={15} className="text-primary-600 flex-shrink-0" />
                <h3 className="text-sm font-bold text-neutral-800">Welcher Typ passt?</h3>
            </div>
            <div className="space-y-1.5">
                {DECISION_ROWS.map((row) => (
                    <div key={row.q} className="flex items-baseline justify-between gap-3 text-xs">
                        <span className="text-neutral-600">{row.q}</span>
                        <span className="font-semibold text-primary-700 text-right flex-shrink-0">{row.a}</span>
                    </div>
                ))}
            </div>
            </div>

          {/* Rolling average */}
          <Section icon={BarChart3} title="Konstanter Durchschnitt" tagline="Für gleichmäßige Ausgaben">
            <p className="text-xs text-neutral-600 leading-relaxed">
              Die Vorhersage entspricht dem Mittelwert der letzten sechs Monate (gestrichelte Linie).
              Diese Schätzung ist sehr stabil und gleicht ausreißende Monate aus.
            </p>
            <ChartCard
              legend={[
                { color: COLORS.history, label: 'Bisherige Monate' },
                { color: COLORS.forecast, label: 'Vorhersage' }
              ]}
            >
              <MiniChart
                bars={[
                  { v: 415, kind: 'history' },
                  { v: 420, kind: 'history' },
                  { v: 450, kind: 'history' },
                  { v: 180, kind: 'history' },
                  { v: 430, kind: 'history' },
                  { v: 410, kind: 'history' },
                  { v: 385, kind: 'forecast', label: '≈ 385 €' }
                ]}
                months={['Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep']}
                emphasizeMonths={[6]}
                refLine={{ v: 385, color: '#93c5fd' }}
              />
            </ChartCard>
          </Section>

          {/* SES */}
          <Section icon={TrendingUp} title="Aktuelles Niveau" tagline="Wenn sich kürzlich etwas geändert hat">
            <p className="text-xs text-neutral-600 leading-relaxed">
              Neuere Monate werden deutlich stärker gewichtet als ältere. Die Vorhersage folgt dem neuen
              Ausgaben deutlich schneller als der konstante Durchschnitt.
            </p>
            <ChartCard
              legend={[
                { color: COLORS.history, label: 'Bisherige Monate' },
                { color: COLORS.forecast, label: 'Vorhersage' }
              ]}
            >
              <MiniChart
                bars={[
                  { v: 180, kind: 'history' },
                  { v: 190, kind: 'history' },
                  { v: 175, kind: 'history' },
                  { v: 185, kind: 'history' },
                  { v: 320, kind: 'history' },
                  { v: 340, kind: 'history' },
                  { v: 330, kind: 'history' },
                  { v: 335, kind: 'history' },
                  { v: 300, kind: 'forecast', label: '≈ 300 €' }
                ]}
                months={['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep']}
                emphasizeMonths={[8]}
                refLine={{ v: 257, label: 'Vgl. Konstanter Ø', color: '#94a3b8' }}
              />
            </ChartCard>
          </Section>

          {/* Seasonal */}
          <Section icon={CalendarRange} title="Saisonale Schwankung" tagline="Für jährlich wiederkehrende Muster">
            <p className="text-xs text-neutral-600 leading-relaxed">
               Ideal, wenn Ausgaben übers Jahr einem festen Muster folgen.
            </p>
            <ChartCard
              legend={[
                { color: COLORS.history, label: 'Bisherige Monate' },
                { color: COLORS.source, label: 'Gleicher Monat im Vorjahr' },
                { color: COLORS.forecast, label: 'Vorhersage' }
              ]}
            >
              <MiniChart
                bars={[
                  { v: 310, kind: 'source' },
                  { v: 280, kind: 'history' },
                  { v: 220, kind: 'history' },
                  { v: 140, kind: 'history' },
                  { v: 90, kind: 'history' },
                  { v: 60, kind: 'history' },
                  { v: 55, kind: 'history' },
                  { v: 65, kind: 'history' },
                  { v: 100, kind: 'history' },
                  { v: 170, kind: 'history' },
                  { v: 240, kind: 'history' },
                  { v: 300, kind: 'history' },
                  { v: 310, kind: 'forecast' }
                ]}
                months={['Jan', '', 'Mär', '', 'Mai', '', 'Jul', '', 'Sep', '', 'Nov', '', 'Jan']}
                emphasizeMonths={[0, 12]}
              />
            </ChartCard>
          </Section>

          {/* None */}
          <Section icon={EyeOff} title="Nicht vorhersagen" tagline="Für Unregelmäßiges und Einnahmen">
            <p className="text-xs text-neutral-600 leading-relaxed">
              Für seltene oder unvorhersehbare Ausgaben (Reparaturen, Möbel, Arztkosten) ist keine
              Vorhersage meist die bessere Wahl. Die Prognose würde jeden Monat einen Betrag
              einplanen, der so nie anfällt.
            </p>
          </Section>

          {/* Refunds & the analytics flag */}
          <div className="bg-neutral-50 rounded-2xl p-4 space-y-1.5">
            <h3 className="text-sm font-bold text-neutral-800">Erstattungen &amp; Einnahmen</h3>
            <p className="text-xs text-neutral-600 leading-relaxed">
              Positive Beträge (z.B. eine Rückerstattung) verringern die berechneten Ausgaben einer
              Kategorie – in Auswertungen und Vorhersagen zählt der Saldo des Monats. Kategorien für
              Einnahmen wie Gehalt sollten über „In Auswertungen &amp; Vorhersagen einbeziehen"
              ausgenommen werden, damit sie die Ausgabenstatistik nicht verfälschen.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PredictionHelpModal;
